import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0dcd1dde"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["content"]
const _hoisted_2 = ["content"]
const _hoisted_3 = { class: "flex flex-column flex-grow-1" }
const _hoisted_4 = { class: "panel-frame mt-s3" }
const _hoisted_5 = { class: "flex flex-column md:flex-row md:align-items-center justify-content-between" }
const _hoisted_6 = { class: "flex justify-content-between align-items-center flex-grow-1 mb-s3" }
const _hoisted_7 = { class: "flex align-items-center" }
const _hoisted_8 = { class: "h3 text-bold" }
const _hoisted_9 = {
  key: 0,
  class: "flex align-items-center mb-s3 gutter-x-s3 sm:ml-s0"
}
const _hoisted_10 = { class: "flex-grow-1" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = {
  key: 3,
  class: "text-center panel-base justify-content-center text-muted"
}
const _hoisted_13 = ["innerHTML"]

import { computed, nextTick, onBeforeUpdate, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { Head } from '@vueuse/head';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import compareMixin from '@/mixins/compare.mixin';
import { ENABLE_CROSS_CHAIN } from '@/helpers/crossChainEnv.helper';
import { PORTFOLIOS_FILTER_BY } from '@/views/pages/liquidity/portfolios/portfolio/models/constants';
import { NETWORK_SUPPORTS_SINGLE_SIDE } from '@/helpers/supporting-features-in-network';
import { GITBOOK_LINKS } from '@/constants/EXTERNAL_LINKS';
import Switch from '@/components/switch/Switch.vue';
import Portfolio from './portfolio/Portfolio.vue';
import SearchInput from '@/components/form/SearchInput.vue';
import PortfoliosFilter from '@/views/pages/liquidity/portfolios/portfolio/portfoliosFilter/portfoliosFilter.vue';
import PortfoliosLoadingPlaceholder from '@/views/pages/liquidity/portfolios/PortfoliosLoadingPlaceholder.vue';
import SortingListDropdown from '@/components/SortingListDropdown.vue';
import PortfolioSteps from '@/views/pages/liquidity/portfolios/PortfolioSteps.vue';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useEasyModeForm } from './portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import InfoMessage from '@/components/InfoMessage.vue';
import SingleSideCarousel from '@/views/pages/liquidity/single-side/components/SingleSideCarousel.vue';
import { Portfolio as TPortfolio } from '@/sdk/entities/portfolio';
import { usePortfoliosSEO } from '@/composables/portfolios/usePortfoliosSEO';
import { usePortfolios } from '@/composables/portfolios/usePortfolios';
import { usePortfoliosFiltering } from '@/composables/portfolios/usePortfoliosFiltering';
import { usePortfoliosSorting } from '@/composables/portfolios/usePortfoliosSorting';
import { usePortfoliosActions } from '@/composables/portfolios/usePortfoliosActions';
import { usePortfoliosUpdater } from '@/composables/portfolios/usePortfoliosUpdater';
import { SORT_FIELD, usePortfoliosFilter } from '@/store/modules/portfolios/usePortfoliosFilter';
import SectionHeader from '@/components/SectionHeader.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Portfolios',
  setup(__props) {

const { compareAddresses } = compareMixin.methods;

const { t } = useI18n();
const route = useRoute();
const { state } = useStore();

const { title, description, keywords } = usePortfoliosSEO();
const { walletState } = useEVMWallet();
const { easyModeForm } = useEasyModeForm();
const { getPortfolios, isPortfoliosUpdated, isPortfoliosStatisticLoaded, isPortfoliosStoreReady } =
  usePortfolios();
const { sortPortfoliosBy } = usePortfoliosSorting();
const { filterByPortfoliosFilter, filterByStakeOnly, filterBySearchText } =
  usePortfoliosFiltering();
const { isSomeInProgress, isStepsShown, onHideSteps, sendTransaction } = usePortfoliosActions();
const { isEnabledPortfoliosUpdater, enablePortfoliosUpdater } = usePortfoliosUpdater();

const {
  sortModel,
  setDefault: setDefaultFilter,
  filterBy,
  filterSearch,
  isStakedOnly,
} = usePortfoliosFilter();

const sortFields: { label: string; value: string }[] = [];
sortFields[SORT_FIELD.LIQUIDITY_IN_USD] = {
  label: t('liquidityIn') + 'USD',
  value: 'liquidityInUSD',
};
sortFields[SORT_FIELD.APR] = { label: t('portfolio.apr.caption'), value: 'apr' };

setDefaultFilter(sortFields[SORT_FIELD.LIQUIDITY_IN_USD].value);

const portfolioRefs = ref<Record<string, Element>>({});
const needScrollToPortfolio = ref(false);
const sortedPortfolios = ref<TPortfolio[] | null>(null);
const selectedPortfolioAddress = ref('');

const action = computed(() => easyModeForm.action);

const searchInputPlaceholder = computed(() => {
  return state.layout.isMobile ? t('search') : t('portfolio.searchPlaceholder');
});

const isPortfoliosSorted = computed(() => {
  return sortedPortfolios.value !== null;
});

const isSortedPortfoliosFound = computed(() => {
  return (sortedPortfolios.value?.length ?? -1) > 0;
});

const isPortfoliosLoading = computed(() => {
  return (
    !isPortfoliosSorted.value || (!isPortfoliosStoreReady.value && !isSortedPortfoliosFound.value)
  );
});

const isNoSearchResults = computed(() => {
  return !isSortedPortfoliosFound.value && !!filterSearch.value;
});

const noPortfoliosMessage = computed(() => {
  const noSearchResults = t('portfolio.noSearchResults', {
    searchText: filterSearch.value,
    escapeParameterHtml: true,
  });
  const noStakedPortfolios = t('portfolio.emptyList', { escapeParameterHtml: true });
  return isNoSearchResults.value ? noSearchResults : noStakedPortfolios;
});

function scrollToPortfolio(portfolioAddress: string) {
  const el = portfolioRefs.value[portfolioAddress];
  if (el) {
    const top = el.getBoundingClientRect().top + window.pageYOffset - 60;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

function onSearch(event) {
  filterSearch.value = event;
  onSort();
}

function onSort() {
  let portfolios: TPortfolio[] = getPortfolios.value;

  portfolios = filterByPortfoliosFilter(portfolios, PORTFOLIOS_FILTER_BY[filterBy.value]);

  if (isStakedOnly.value) {
    portfolios = filterByStakeOnly(portfolios);
  }

  if (filterSearch.value) {
    const searchText = filterSearch.value.toLowerCase().trim();
    portfolios = filterBySearchText(portfolios, searchText);
  }

  sortedPortfolios.value = sortPortfoliosBy(portfolios, sortModel.sortBy, sortModel.direction);
}

function onFilter(event) {
  filterBy.value = event;
  onSort();
}

function enableCrossChain() {
  return ENABLE_CROSS_CHAIN;
}

onBeforeUpdate(() => {
  portfolioRefs.value = {};
});

watch(isPortfoliosStoreReady, () => {
  onSort();
});

watch(isStakedOnly, () => {
  sortedPortfolios.value = null;
  onSort();
});

// NOTE: It invokes after UPDATE_PORTFOLIOS_INFO.
watch(isPortfoliosUpdated, () => {
  if (isPortfoliosStoreReady.value && isPortfoliosUpdated.value) {
    onSort();
    if (needScrollToPortfolio.value) {
      needScrollToPortfolio.value = false;
      nextTick(() => scrollToPortfolio(selectedPortfolioAddress.value));
    }
  }
});

// NOTE:
// It invokes after first call of GET_PORTFOLIO_STATISTICS (init app) on Portfolios page
// and when change route to Portfolios page
watch(
  isPortfoliosStatisticLoaded,
  async () => {
    if (!isEnabledPortfoliosUpdater.value) {
      enablePortfoliosUpdater();
    }

    if (!isPortfoliosUpdated.value) {
      needScrollToPortfolio.value = true;
      return;
    }
    onSort();
    await nextTick(() => scrollToPortfolio(selectedPortfolioAddress.value));
  },
  {
    immediate: true,
  },
);

watch(
  () => route.params,
  () => {
    if (!route.params || !route.params.address) {
      return;
    }

    const portfolioAddress = Array.isArray(route.params.address)
      ? route.params.address[0]
      : route.params.address;

    selectedPortfolioAddress.value = portfolioAddress;

    if (isPortfoliosStatisticLoaded.value) {
      nextTick(() => scrollToPortfolio(portfolioAddress));
    }
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Head), null, {
      default: _withCtx(() => [
        _createElementVNode("title", null, _toDisplayString(_unref(title)), 1),
        _createElementVNode("meta", {
          property: "description",
          name: "description",
          content: _unref(description)
        }, null, 8, _hoisted_1),
        _createElementVNode("meta", {
          property: "keywords",
          name: "keywords",
          content: _unref(keywords)
        }, null, 8, _hoisted_2)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(SectionHeader, {
        section: "portfolios",
        "how-link": _unref(GITBOOK_LINKS).PORTFOLIOS
      }, null, 8, ["how-link"]),
      (_unref(NETWORK_SUPPORTS_SINGLE_SIDE))
        ? (_openBlock(), _createBlock(SingleSideCarousel, { key: 0 }))
        : _createCommentVNode("", true),
      (_unref(isStepsShown))
        ? (_openBlock(), _createBlock(PortfolioSteps, {
            key: 1,
            action: _unref(action),
            class: "mb-s3",
            onOnClose: _cache[0] || (_cache[0] = ($event: any) => (_unref(onHideSteps)()))
          }, null, 8, ["action"]))
        : _createCommentVNode("", true),
      (_unref(isSomeInProgress))
        ? (_openBlock(), _createBlock(InfoMessage, {
            key: 2,
            type: "warning",
            message: _ctx.$t('wrappedContract.steps.infoMessage.someInProgress'),
            "as-panel": ""
          }, null, 8, ["message"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t('portfolios')), 1)
            ]),
            (_unref(walletState).isInjected && _unref(isPortfoliosStatisticLoaded))
              ? (_openBlock(), _createBlock(Switch, {
                  key: 0,
                  name: _ctx.$t('portfolio.providedOnly'),
                  "name-pos": "left",
                  modelValue: _unref(isStakedOnly),
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(isStakedOnly) ? (isStakedOnly).value = $event : null))
                }, null, 8, ["name", "modelValue"]))
              : _createCommentVNode("", true)
          ]),
          (_unref(isPortfoliosStatisticLoaded))
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(SortingListDropdown, {
                    "sort-fields": sortFields,
                    "sort-by": _unref(sortModel).sortBy,
                    "onUpdate:sort-by": _cache[2] || (_cache[2] = ($event: any) => ((_unref(sortModel).sortBy) = $event)),
                    direction: _unref(sortModel).direction,
                    "onUpdate:direction": _cache[3] || (_cache[3] = ($event: any) => ((_unref(sortModel).direction) = $event)),
                    onChange: onSort,
                    class: _normalizeClass(_unref(state).layout.isMobile ? 'sorting-dropdown' : '')
                  }, null, 8, ["sort-by", "direction", "class"])
                ]),
                (enableCrossChain())
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createVNode(PortfoliosFilter, {
                        onChange: _cache[4] || (_cache[4] = ($event: any) => (onFilter($event)))
                      })
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]),
        (_unref(isSortedPortfoliosFound) || _unref(isNoSearchResults))
          ? (_openBlock(), _createBlock(SearchInput, {
              key: 0,
              modelValue: _unref(filterSearch),
              "onUpdate:modelValue": [
                _cache[5] || (_cache[5] = ($event: any) => (_isRef(filterSearch) ? (filterSearch).value = $event : null)),
                _cache[6] || (_cache[6] = ($event: any) => (onSearch($event)))
              ],
              placeholder: _unref(searchInputPlaceholder),
              class: "w-full mb-s3"
            }, null, 8, ["modelValue", "placeholder"]))
          : _createCommentVNode("", true),
        (_unref(isPortfoliosLoading))
          ? (_openBlock(), _createBlock(PortfoliosLoadingPlaceholder, { key: 1 }))
          : (_unref(isSortedPortfoliosFound))
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(sortedPortfolios.value, (portfolio, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: portfolio.contractAddress,
                  ref_for: true,
                  ref: 
            el => {
              if (el) portfolioRefs.value[portfolio.contractAddress] = el as Element;
            }
          
                }, [
                  _createVNode(Portfolio, {
                    portfolio: (portfolio as TPortfolio),
                    class: _normalizeClass(index !== 0 ? 'mt-s3' : ''),
                    "open-after-created": 
              _unref(compareAddresses)(selectedPortfolioAddress.value, portfolio.contractAddress)
            ,
                    onSendTransaction: _unref(sendTransaction)
                  }, null, 8, ["portfolio", "class", "open-after-created", "onSendTransaction"])
                ]))
              }), 128))
            : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("span", { innerHTML: _unref(noPortfoliosMessage) }, null, 8, _hoisted_13)
              ]))
      ])
    ])
  ], 64))
}
}

})